import { ReactNode } from "react";

import {
  BooleanIcon,
  Box,
  MeetingIcon,
  NullIcon,
  NumberIcon,
  Row,
  Text,
  TextStringIcon,
} from "@hightouch/htui";

import { ExtendedOption } from "@hightouch/formkit";
import {
  FormkitModel,
  JOURNEY_CONTEXT_VARIABLE_VALUE_PREFIX,
  ColumnOption,
} from "./formkit-context";
import { MappingType, StaticType } from "./types";
import { SyncableColumnFragment as SyncableColumn } from "src/graphql";

/**
 * Resolve the raw source column name from a string mapping `from`, stripping
 * the array-expansion element wrapper (`_ht_tags_element` → `tags`). Other
 * strings pass through unchanged; non-string `from` values return `null`.
 *
 * Intentionally string-only. Callers like the column-selector `GroupedCombobox`
 * rely on non-strings returning `null` so they fall back to the raw `from`
 * object, which the combobox deep-matches against its object-valued trait
 * options — returning a resolved string here would leave those options
 * unselected. To resolve a trait reference to its column name, call
 * `resolveTraitReference` explicitly (see `ArrayExpansionSection`).
 */
export function resolveSourceColumn(mappingFrom: unknown): string | null {
  if (typeof mappingFrom !== "string") return null;
  const match = mappingFrom.match(/^_ht_(.+)_element$/);
  return match?.[1] ?? mappingFrom;
}

/**
 * Resolve the friendly column name of an audience-trait reference object, or
 * `null` if `mappingFrom` isn't one of the recognized trait-reference shapes
 * (`additionalColumnReference` or a `transformed`/`trait` column). This is the
 * single source of truth for what counts as a trait reference — both
 * `isTraitReference` and `ArrayExpansionSection`'s array-column detection
 * delegate here. Kept separate from `resolveSourceColumn` so trait resolution
 * never leaks into its string-only callers.
 */
export function resolveTraitReference(mappingFrom: unknown): string | null {
  if (mappingFrom == null || typeof mappingFrom !== "object") {
    return null;
  }

  const ref = mappingFrom as Record<string, unknown>;

  if (ref.type === "additionalColumnReference") {
    return typeof ref.columnAlias === "string" ? ref.columnAlias : null;
  }

  if (ref.type === "transformed") {
    if (ref.column == null || typeof ref.column !== "object") return null;
    const column = ref.column as Record<string, unknown>;
    return column.type === "trait" && typeof column.name === "string"
      ? column.name
      : null;
  }

  return null;
}

/**
 * Whether `mappingFrom` is a recognized audience-trait reference object
 * (`additionalColumnReference` or a `transformed`/`trait` column). Use this to
 * gate trait-specific handling rather than a bare `typeof === "object"` check,
 * which would also match unrelated object shapes.
 */
export function isTraitReference(mappingFrom: unknown): boolean {
  return resolveTraitReference(mappingFrom) !== null;
}

/**
 * Extracts values from grouped column options into a Set.
 * Optionally filters options before extraction.
 *
 * @param columnOptions - Array of grouped column options
 * @param options.filter - Optional predicate to filter which options to include
 * @param options.includeLabels - Whether to include labels in addition to values (default: true)
 * @returns Set of extracted string values
 */
export function extractColumnValues(
  columnOptions: ColumnOption[] | undefined,
  filter?: (opt: ColumnOption) => boolean,
): Set<string> {
  const values = new Set<string>();

  for (const group of columnOptions ?? []) {
    for (const opt of group.options ?? []) {
      if (filter && !filter(opt)) continue;
      if (typeof opt.value === "string") values.add(opt.value);
      if (typeof opt.label === "string") values.add(opt.label);
    }
  }

  return values;
}

export function formatValue(
  value: any,
  options?: { prefixToRemove?: string; columnOptions?: ColumnOption[] },
): ReactNode {
  if (typeof value === "object") {
    if (value.variable) {
      return value.variable;
    }
    if (value.type === MappingType.OBJECT) {
      return "Object:";
    }
    if (value.type === MappingType.ARRAY) {
      return (
        <Row alignItems="center" gap="3px">
          <Text color="teal.700">Array:</Text>
          <Text color="gray.600" fontWeight="normal" isTruncated>
            {value.from
              ? resolveFromValue(value.from)
              : value.type === MappingType.ARRAY
                ? "Pick an array from the list."
                : "Enter an array field."}
          </Text>
        </Row>
      );
    }

    if (
      value.type === MappingType.STANDARD &&
      typeof value.from === "string" &&
      value.from
    ) {
      // Only derive "expected column" status when the caller provided column options.
      // If provided (even if empty), treat any unknown `from` as an expected column.
      // For array expansion element columns (_ht_X_element), resolve back to
      // the source column name — the element column is virtual and won't
      // appear in the known columns set.
      if (options?.columnOptions) {
        const known = extractColumnValues(options.columnOptions);
        const resolvedFrom = resolveSourceColumn(value.from) ?? value.from;
        if (!known.has(resolvedFrom)) {
          return (
            <Row alignItems="center" gap="3px">
              <Text>{resolveFromValue(value.from)}</Text>
              <Box sx={{ span: { fontWeight: "300" } }}>
                <Text color="gray.600" isTruncated>
                  (expected column)
                </Text>
              </Box>
            </Row>
          );
        }
        if (resolvedFrom !== value.from) {
          return <Text>{resolvedFrom}</Text>;
        }
      }
    }

    if (value.from) {
      return resolveFromValue(value.from, options);
    }

    // If a trait is mapped in the association wrapper, it won't have a from value, so resolve the entire value
    if (value.column) {
      return resolveFromValue(value);
    }

    if (typeof value.value === "undefined") {
      return value.template;
    } else {
      return String(value.value);
    }
  }

  return value;
}

export function formatStaticTraitLabel(name: string, value: string): string {
  return `${name} (${value})`;
}

export function isTraitEnrichment(type: string | undefined): boolean {
  return type === "additionalColumnReference";
}

export function isSplitColumn(type: string | undefined): boolean {
  return type === "splitTest";
}

export function isJourneyVariableColumn(
  columnName: string | undefined,
): boolean {
  return (
    // Context columns are raw columns.
    // To avoid confusion, a prefix is added to the column name on the backend.
    typeof columnName === "string" &&
    Boolean(columnName?.startsWith(JOURNEY_CONTEXT_VARIABLE_VALUE_PREFIX))
  );
}

export function isSyncableJourneyVariableColumn(
  column: SyncableColumn | undefined,
): boolean {
  return Boolean(column?.metadata?.is_context_variable);
}

export function replacePrefix(
  value: string,
  prefix: string,
  replacement?: string,
): string {
  return value.startsWith(prefix)
    ? `${replacement ?? ""}${value.slice(prefix.length)}`
    : value;
}

export function resolveFromValue(
  fromValue: any,
  options?: { prefixToRemove?: string },
): string {
  if (typeof fromValue === "object") {
    if (Array.isArray(fromValue)) {
      return fromValue.map((value) => formatValue(value, options)).join(", ");
    }

    if (fromValue.type === "boosted") {
      return fromValue.semanticType;
    }
    if (isTraitEnrichment(fromValue.type) || isSplitColumn(fromValue.type)) {
      return fromValue.columnAlias;
    }
    if (fromValue.type === "related" || fromValue.type === "transformed") {
      return fromValue.column.name;
    }

    return typeof fromValue.name === "string"
      ? fromValue.name
      : "Unknown column";
  }

  if (typeof fromValue === "string" && options?.prefixToRemove) {
    return replacePrefix(
      fromValue,
      options.prefixToRemove,
      "Journey context variable: ",
    );
  }

  return fromValue;
}

export const NEW_STATIC_ICONS = {
  [StaticType.STRING]: TextStringIcon,
  [StaticType.NUMBER]: NumberIcon,
  [StaticType.BOOLEAN]: BooleanIcon,
  [StaticType.DATETIME]: MeetingIcon,
  [StaticType.NULL]: NullIcon,
};

export function isMatchboostingEnabled({ fields = [] }: { fields?: any[] }) {
  return fields?.some((field) => field?.from?.type === "boosted");
}

export function isMatchBoostingAvailableForSyncConfig({
  options,
  componentSupportsMatchBoosting,
  destinationSupportsMatchBoosting,
}: {
  componentSupportsMatchBoosting?: boolean;
  options?: ExtendedOption[];
  destinationSupportsMatchBoosting?: boolean;
}): boolean {
  // In the case that the component didn't explicitly define it supports match
  // boosting, check if they have any semantic columns defined (which is only
  // used for MB) to infer if this component supports MB
  const containBoostedDestinationOptions =
    options?.some((opt) => opt?.extendedType?.semanticColumnType != null) ??
    false;

  return Boolean(
    // Destination (and possibly the destination type - e.g. segment type) supports MB
    destinationSupportsMatchBoosting &&
      // Destination mappings component supports boosting field
      (componentSupportsMatchBoosting || containBoostedDestinationOptions),
  );
}

export function isMatchBoosterSetUpToUse({
  mbEnabledForWorkspace,
  model,
  lastEnrichedAt,
}: {
  mbEnabledForWorkspace: boolean;
  model?: FormkitModel;
  lastEnrichedAt?: string;
}): boolean {
  return Boolean(
    // MB feature flag is on for this wkspc
    mbEnabledForWorkspace &&
      // Model has MB enabled
      (model?.parent?.matchboosting_enabled || model?.matchboosting_enabled) &&
      // Enrichment finished running (at least one enrichment run is required before ppl can use MB)
      lastEnrichedAt,
  );
}
